import * as React from 'react';
import { IntlShape } from 'react-intl';
import { FIELD_TYPE } from 'app/infrastructure/simple-form/field-types';
import { parseAsDateUTC } from 'app/infrastructure/data/date';
import { SimpleFormComponentProps, SerializeType } from 'app/infrastructure/simple-form/types';
import { ids } from 'app/area/administration/constants';
import { sfNames } from 'app/area/administration/edit/sf-names';
import { User } from 'app/components/access-panel/shared/types';
import { AdminPaperTopPanel } from 'app/area/administration/edit/components';
import { isExternalUserHandlingEnabled } from 'app/infrastructure/hosting';
import { PersonActionType } from 'app/area/administration/shared/types';
import { InfoText } from 'ui';
import styles from 'app/area/administration/edit/user/details.scss';

export interface DetailsProps {
  actionType: PersonActionType;
  showPersonId: boolean;
  data: User;
  readOnly: boolean;
  translation: any;
  intl: IntlShape;
  formComponent: React.ComponentType<SimpleFormComponentProps>;
}

export const Details = (props: DetailsProps) => {
  const {
    formComponent, translation, data, readOnly, showPersonId, actionType
  } = props;

  const SimpleForm = formComponent;

  const externalUserHandlingReadOnly = isExternalUserHandlingEnabled();

  const dateFormatter = (date: string) => {
    const parsedDate = new Date(date);
    const formattedDate = props.intl.formatDate(parsedDate, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    const formattedTime = props.intl.formatTime(parsedDate);
    return formattedDate + ' ' + formattedTime;
  };

  const latestActivity = data && data.latestActivity && dateFormatter(data.latestActivity);

  return <AdminPaperTopPanel title={translation.edit_details_title}>
    <div className="row">
      <div id={ids.users.name} className="col-lg-6 col-sm-12">
        <SimpleForm
          name={sfNames.name}
          maxLength={254}
          type={FIELD_TYPE.TEXTAREA}
          label={{ text: translation.name, required: !readOnly }}
          initialValue={data && data.name}
          serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
          readOnly={readOnly || externalUserHandlingReadOnly}
        />
      </div>

      <div id={ids.users.email_address} className="col-lg-6 col-sm-12">
        <SimpleForm
          name={sfNames.emailAddress}
          maxLength={254}
          type={FIELD_TYPE.TEXTAREA}
          label={{ text: translation.email }}
          initialValue={data && data.emailAddress}
          serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
          readOnly={readOnly || externalUserHandlingReadOnly}
        />
      </div>
    </div>

    <div className="row expanders-in-equal-cols">
      {showPersonId && <div id={ids.users.user_id} className="col-sm-12 col-lg-6">
        <SimpleForm
          name={sfNames.personId}
          maxLength={80}
          type={FIELD_TYPE.TEXTAREA}
          label={{ text: translation.new_user_id, required: !readOnly }}
          initialValue=""
          serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
          readOnly={readOnly}
        />
      </div>}

      <div id={ids.users.disabled_date} className="col-sm-12 col-lg-6">
        <SimpleForm
          name={sfNames.disabled}
          readOnly={readOnly}
          type={FIELD_TYPE.DATE_TIME}
          label={{ text: translation.disabled_date }}
          initialValue={data && data.disabled && parseAsDateUTC(data.disabled)}
          serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
        />
      </div>

      {actionType === PersonActionType.edit &&
        <div id={ids.users.latest_activity} className="col-sm-12 col-lg-6">
          <div className="control-label">
            {translation.latest_activity}
            <InfoText visible={true} text={translation.latest_activity_info_text} />
          </div>
          <div className="input-wrapper">
            <div className="input-group">
              <input
                type="text"
                name={sfNames.latestActivity}
                className={`${styles.latestActivity} ${readOnly ? styles.readOnly : styles.default} form-control`}
                value={latestActivity ?? translation.latest_activity_no_activity}
                disabled />
            </div>
          </div>
        </div>}
    </div>
  </AdminPaperTopPanel>;
};
